// Skills Repository Data
// Centralized Library managed by ABA Head
// Contains: Skill Domains -> Skills -> Targets
// Skills labeled with: Assessment sources (VB-MAPP, ABLLS)

export const skillDomains = [
  {
    id: 'DOMAIN-MAND',
    code: 'MAND',
    name: 'Mand',
    description: 'Requesting skills - The ability to request desired items, actions, or information',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-TACT',
    code: 'TACT',
    name: 'Tact',
    description: 'Labeling skills - The ability to name or identify items, actions, or events',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-LISTENER',
    code: 'LISTENER',
    name: 'Listener Responding',
    description: 'Following instructions and responding to verbal stimuli',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-VP-MTS',
    code: 'VP-MTS',
    name: 'Visual Perceptual Skills & Matching-to-Sample',
    description: 'Visual discrimination and matching skills',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-PLAY',
    code: 'PLAY',
    name: 'Independent Play',
    description: 'Ability to play independently with toys and materials',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-SOCIAL',
    code: 'SOCIAL',
    name: 'Social Behavior & Social Play',
    description: 'Social interaction and play skills with peers and adults',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-ECHOIC',
    code: 'ECHOIC',
    name: 'Echoic',
    description: 'Ability to repeat vocal sounds and words',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-INTRAVERBAL',
    code: 'INTRAVERBAL',
    name: 'Intraverbal',
    description: 'Conversational skills - responding to questions and fill-ins',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-COOP',
    code: 'COOP',
    name: 'Cooperation & Reinforcer Effectiveness',
    description: 'Ability to cooperate with the instructor and the effectiveness of reinforcers in maintaining responding',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-CLASSROOM',
    code: 'CLASSROOM',
    name: 'Classroom & Group Skills',
    description: 'Skills required to function in classroom and group instruction settings, including following group instructions and routines',
    assessmentSources: ['VB-MAPP', 'ABLLS']
  },
  {
    id: 'DOMAIN-ACADEMIC',
    code: 'ACADEMIC',
    name: 'Academic',
    description: 'Academic skills including Mathematics, English, Reading, and Writing',
    assessmentSources: ['Curriculum-Based', 'Teacher Assessment']
  }
];

// Skills Data Structure:
// id: Unique skill identifier (SK-DOMAIN-XXX format)
// domain: Domain ID reference
// name: Short skill name/description
// labels: Array of assessment source labels (e.g., ['VB-MAPP-1a'], ['ABLLS-G1'])
// level: VB-MAPP milestone level (1, 2, or 3)
// targets: Array of specific targets that break down this skill (optional)

export const skillsData = [
  // ==========================================
  // SKILL DOMAIN: MAND/REQUESTING
  // ==========================================

  // Level 1 Mand Skills (Milestones 1-5)
  {
    id: 'SK-MAND-001',
    domain: 'DOMAIN-MAND',
    name: 'Makes eye contact (gaze shift) as mand for attention 2 times',
    labels: ['VB-MAPP-1a'],
    level: 1
  },
  {
    id: 'SK-MAND-002',
    domain: 'DOMAIN-MAND',
    name: 'Moves close to reinforcing item to indicate MO 2 times',
    labels: ['VB-MAPP-1b'],
    level: 1
  },
  {
    id: 'SK-MAND-003',
    domain: 'DOMAIN-MAND',
    name: 'Emits 2 spontaneous mands (no verbal prompt)',
    labels: ['VB-MAPP-1c'],
    level: 1
  },
  {
    id: 'SK-MAND-004',
    domain: 'DOMAIN-MAND',
    name: 'Emits 2 words/signs/PECS (may need prompts, no physical)',
    labels: ['VB-MAPP-1b', 'ABLLS-G1'],
    level: 1
  },
  {
    id: 'SK-MAND-005',
    domain: 'DOMAIN-MAND',
    name: 'Pulls an adult to a reinforcing item 2 times',
    labels: ['VB-MAPP-1c'],
    level: 1
  },
  {
    id: 'SK-MAND-006',
    domain: 'DOMAIN-MAND',
    name: 'Points or gestures towards a reinforcer to obtain it 2 times',
    labels: ['VB-MAPP-1d'],
    level: 1
  },
  {
    id: 'SK-MAND-007',
    domain: 'DOMAIN-MAND',
    name: 'Points to 2 different reinforcers to obtain them 2 times',
    labels: ['VB-MAPP-2a'],
    level: 1
  },
  {
    id: 'SK-MAND-008',
    domain: 'DOMAIN-MAND',
    name: 'Nods or says YES/NO when offered a reinforcer 2 times',
    labels: ['VB-MAPP-2b'],
    level: 1
  },
  {
    id: 'SK-MAND-009',
    domain: 'DOMAIN-MAND',
    name: 'Emits 2 mands without echoic prompts (verbal or object prompts allowed)',
    labels: ['VB-MAPP-2c'],
    level: 1
  },
  {
    id: 'SK-MAND-010',
    domain: 'DOMAIN-MAND',
    name: 'Mands for 20 different items without prompts',
    labels: ['VB-MAPP-6a'],
    level: 2
  },
  {
    id: 'SK-MAND-011',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously emits 1 mand without echoic/imitative prompts',
    labels: ['VB-MAPP-2d'],
    level: 1
  },
  {
    id: 'SK-MAND-012',
    domain: 'DOMAIN-MAND',
    name: 'Generalizes 2 mands across 2 people and 2 settings',
    labels: ['VB-MAPP-2e'],
    level: 1
  },
  {
    id: 'SK-MAND-013',
    domain: 'DOMAIN-MAND',
    name: 'Emits 4 different mands without prompts (except What do you want?)',
    labels: ['VB-MAPP-2M'],
    level: 1
  },
  {
    id: 'SK-MAND-014',
    domain: 'DOMAIN-MAND',
    name: 'Emits 5 mands without echoic/imitative prompts',
    labels: ['VB-MAPP-3b'],
    level: 1
  },
  {
    id: 'SK-MAND-015',
    domain: 'DOMAIN-MAND',
    name: 'Generalizes 3 mands across 2 examples of desired item',
    labels: ['VB-MAPP-3c'],
    level: 1
  },
  {
    id: 'SK-MAND-016',
    domain: 'DOMAIN-MAND',
    name: 'Emits 6 different mands without echoic/imitative prompts',
    labels: ['VB-MAPP-3d'],
    level: 1
  },
  {
    id: 'SK-MAND-017',
    domain: 'DOMAIN-MAND',
    name: 'Generalizes 6 mands across 2 people, 2 settings, and 2 examples',
    labels: ['VB-MAPP-3M'],
    level: 1
  },
  {
    id: 'SK-MAND-018',
    domain: 'DOMAIN-MAND',
    name: 'Mands for attention 2 times with identifiable response',
    labels: ['VB-MAPP-4a'],
    level: 1
  },
  {
    id: 'SK-MAND-019',
    domain: 'DOMAIN-MAND',
    name: 'Emits 2 mands without object present',
    labels: ['VB-MAPP-4b'],
    level: 1
  },
  {
    id: 'SK-MAND-020',
    domain: 'DOMAIN-MAND',
    name: 'Emits 2 mands without verbal prompts (object prompts allowed)',
    labels: ['VB-MAPP-4c'],
    level: 1
  },
  {
    id: 'SK-MAND-021',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to attend to same stimulus 5 times per day',
    labels: ['VB-MAPP-4d'],
    level: 1
  },
  {
    id: 'SK-MAND-022',
    domain: 'DOMAIN-MAND',
    name: 'Emits 1 mand to remove undesirable item/activity',
    labels: ['VB-MAPP-4e'],
    level: 1
  },
  {
    id: 'SK-MAND-023',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously emits 5 mands (no verbal prompts) in 60 mins',
    labels: ['VB-MAPP-4M'],
    level: 1
  },
  {
    id: 'SK-MAND-024',
    domain: 'DOMAIN-MAND',
    name: 'Emits 3 different mands without object present',
    labels: ['VB-MAPP-5a'],
    level: 1
  },
  {
    id: 'SK-MAND-025',
    domain: 'DOMAIN-MAND',
    name: 'Emits 3 different mands without verbal prompts',
    labels: ['VB-MAPP-5b'],
    level: 1
  },
  {
    id: 'SK-MAND-026',
    domain: 'DOMAIN-MAND',
    name: 'Mands with varied intonation appropriate to MO 2 times',
    labels: ['VB-MAPP-5c'],
    level: 1
  },
  {
    id: 'SK-MAND-027',
    domain: 'DOMAIN-MAND',
    name: 'Acquires new mand in less than 20 training trials',
    labels: ['VB-MAPP-5d'],
    level: 1
  },
  {
    id: 'SK-MAND-028',
    domain: 'DOMAIN-MAND',
    name: 'Persists with mand 2 times if reinforcer not delivered',
    labels: ['VB-MAPP-5e'],
    level: 1
  },
  {
    id: 'SK-MAND-029',
    domain: 'DOMAIN-MAND',
    name: 'Emits 10 different mands without prompts',
    labels: ['VB-MAPP-5M'],
    level: 1
  },

  // Level 2 Mand Skills (Milestones 6-10)
  {
    id: 'SK-MAND-030',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously emits 5 mands without object/verbal prompts',
    labels: ['VB-MAPP-6a'],
    level: 2
  },
  {
    id: 'SK-MAND-031',
    domain: 'DOMAIN-MAND',
    name: 'Emits 10 different mands without echoic/imitative prompts',
    labels: ['VB-MAPP-6b'],
    level: 2
  },
  {
    id: 'SK-MAND-032',
    domain: 'DOMAIN-MAND',
    name: 'Mands for 5 different missing items without prompts',
    labels: ['VB-MAPP-6c'],
    level: 2
  },
  {
    id: 'SK-MAND-033',
    domain: 'DOMAIN-MAND',
    name: 'Generalizes 4 mands to 4 different people',
    labels: ['VB-MAPP-6d'],
    level: 2
  },
  {
    id: 'SK-MAND-034',
    domain: 'DOMAIN-MAND',
    name: 'Maintains newly acquired mand after 24 hours no contact',
    labels: ['VB-MAPP-6e'],
    level: 2
  },
  {
    id: 'SK-MAND-035',
    domain: 'DOMAIN-MAND',
    name: 'Mands for 20 different missing items without prompts',
    labels: ['VB-MAPP-6M'],
    level: 2
  },
  {
    id: 'SK-MAND-036',
    domain: 'DOMAIN-MAND',
    name: 'Mands 2 times with 2 words in phrase/sentence',
    labels: ['VB-MAPP-7a'],
    level: 2
  },
  {
    id: 'SK-MAND-037',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to emit 2 different actions',
    labels: ['VB-MAPP-7b'],
    level: 2
  },
  {
    id: 'SK-MAND-038',
    domain: 'DOMAIN-MAND',
    name: 'Emits 2 mands to remove undesirable items/activities',
    labels: ['VB-MAPP-7c'],
    level: 2
  },
  {
    id: 'SK-MAND-039',
    domain: 'DOMAIN-MAND',
    name: 'Demonstrates response generalization for 2 different mands',
    labels: ['VB-MAPP-7d'],
    level: 2
  },
  {
    id: 'SK-MAND-040',
    domain: 'DOMAIN-MAND',
    name: 'Mands with varied intonation for positive/negative MOs 5 times',
    labels: ['VB-MAPP-7e'],
    level: 2
  },
  {
    id: 'SK-MAND-041',
    domain: 'DOMAIN-MAND',
    name: 'Mands for help or assistance 2 times',
    labels: ['VB-MAPP-7f', 'ABLLS-F12'],
    level: 2
  },
  {
    id: 'SK-MAND-042',
    domain: 'DOMAIN-MAND',
    name: 'Demonstrates high frequency manding (15 in 5 minutes)',
    labels: ['VB-MAPP-7g'],
    level: 2
  },
  {
    id: 'SK-MAND-043',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to emit 5 different actions for activity',
    labels: ['VB-MAPP-7M'],
    level: 2
  },
  {
    id: 'SK-MAND-044',
    domain: 'DOMAIN-MAND',
    name: 'Mands 5 times with 2 words in phrase/sentence',
    labels: ['VB-MAPP-8a'],
    level: 2
  },
  {
    id: 'SK-MAND-045',
    domain: 'DOMAIN-MAND',
    name: 'Mands for information 2 times using what questions',
    labels: ['VB-MAPP-8b', 'ABLLS-F19'],
    level: 2
  },
  {
    id: 'SK-MAND-046',
    domain: 'DOMAIN-MAND',
    name: 'Mands with pronoun 2 times',
    labels: ['VB-MAPP-8c'],
    level: 2
  },
  {
    id: 'SK-MAND-047',
    domain: 'DOMAIN-MAND',
    name: 'Mands with 3 different carrier phrases',
    labels: ['VB-MAPP-8d'],
    level: 2
  },
  {
    id: 'SK-MAND-048',
    domain: 'DOMAIN-MAND',
    name: 'Mands with yes and no 4 times',
    labels: ['VB-MAPP-8e', 'ABLLS-F10'],
    level: 2
  },
  {
    id: 'SK-MAND-049',
    domain: 'DOMAIN-MAND',
    name: 'Mands for information 2 times using where questions',
    labels: ['VB-MAPP-8f', 'ABLLS-F20'],
    level: 2
  },
  {
    id: 'SK-MAND-050',
    domain: 'DOMAIN-MAND',
    name: 'Emits 5 different mands with 2+ words (not I want)',
    labels: ['VB-MAPP-8M'],
    level: 2
  },
  {
    id: 'SK-MAND-051',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously emits 50 mands per day',
    labels: ['VB-MAPP-9a'],
    level: 2
  },
  {
    id: 'SK-MAND-052',
    domain: 'DOMAIN-MAND',
    name: 'Acquires 2 mands from tact/echoic only training',
    labels: ['VB-MAPP-9b'],
    level: 2
  },
  {
    id: 'SK-MAND-053',
    domain: 'DOMAIN-MAND',
    name: 'Mands for information 25 times using any question word',
    labels: ['VB-MAPP-9c'],
    level: 2
  },
  {
    id: 'SK-MAND-054',
    domain: 'DOMAIN-MAND',
    name: 'Mands with adjective 5 times',
    labels: ['VB-MAPP-9d', 'ABLLS-F16'],
    level: 2
  },
  {
    id: 'SK-MAND-055',
    domain: 'DOMAIN-MAND',
    name: 'Emits 5 mands in 30 seconds',
    labels: ['VB-MAPP-9e'],
    level: 2
  },
  {
    id: 'SK-MAND-056',
    domain: 'DOMAIN-MAND',
    name: 'Mands contain 3-word phrases 10 times',
    labels: ['VB-MAPP-9f', 'ABLLS-F11'],
    level: 2
  },
  {
    id: 'SK-MAND-057',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously emits 15 different mands in 30 mins',
    labels: ['VB-MAPP-9M'],
    level: 2
  },

  // Level 3 Mand Skills (Milestones 11-15)
  {
    id: 'SK-MAND-058',
    domain: 'DOMAIN-MAND',
    name: 'Emits 2 new mands without training',
    labels: ['VB-MAPP-10a', 'ABLLS-F13'],
    level: 3
  },
  {
    id: 'SK-MAND-059',
    domain: 'DOMAIN-MAND',
    name: 'Demonstrates response generalization for 2 different mands',
    labels: ['VB-MAPP-10b'],
    level: 3
  },
  {
    id: 'SK-MAND-060',
    domain: 'DOMAIN-MAND',
    name: 'Mands with adjective 5 times (prompts allowed)',
    labels: ['VB-MAPP-10c'],
    level: 3
  },
  {
    id: 'SK-MAND-061',
    domain: 'DOMAIN-MAND',
    name: 'Mands for information 3 times using who questions',
    labels: ['VB-MAPP-10d', 'ABLLS-F21'],
    level: 3
  },
  {
    id: 'SK-MAND-062',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously mands for others to emit 20 different actions',
    labels: ['VB-MAPP-10e', 'ABLLS-F8'],
    level: 3
  },
  {
    id: 'SK-MAND-063',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously mands to novel/unfamiliar people 2 times',
    labels: ['VB-MAPP-10f'],
    level: 3
  },
  {
    id: 'SK-MAND-064',
    domain: 'DOMAIN-MAND',
    name: 'Emits 10 new mands without specific training',
    labels: ['VB-MAPP-10M'],
    level: 3
  },
  {
    id: 'SK-MAND-065',
    domain: 'DOMAIN-MAND',
    name: 'Mands to peers 5 times',
    labels: ['VB-MAPP-11a'],
    level: 3
  },
  {
    id: 'SK-MAND-066',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously mands for attention 5 times',
    labels: ['VB-MAPP-11b', 'ABLLS-F14'],
    level: 3
  },
  {
    id: 'SK-MAND-067',
    domain: 'DOMAIN-MAND',
    name: 'Emits 100+ mands in one week',
    labels: ['VB-MAPP-11c'],
    level: 3
  },
  {
    id: 'SK-MAND-068',
    domain: 'DOMAIN-MAND',
    name: 'Mand contains 2 word phrases 10 times',
    labels: ['VB-MAPP-11d'],
    level: 3
  },
  {
    id: 'SK-MAND-069',
    domain: 'DOMAIN-MAND',
    name: 'Mands for specific quantity of reinforcers 2 times',
    labels: ['VB-MAPP-11e'],
    level: 3
  },
  {
    id: 'SK-MAND-070',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously mands for verbal info using WH question 5 times',
    labels: ['VB-MAPP-11M'],
    level: 3
  },
  {
    id: 'SK-MAND-071',
    domain: 'DOMAIN-MAND',
    name: 'Mands to remove aversive item/activity 2 times',
    labels: ['VB-MAPP-12a', 'ABLLS-F15'],
    level: 3
  },
  {
    id: 'SK-MAND-072',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to perform two-step action 2 times',
    labels: ['VB-MAPP-12b'],
    level: 3
  },
  {
    id: 'SK-MAND-073',
    domain: 'DOMAIN-MAND',
    name: 'Says please/thank you with indirect verbal prompts',
    labels: ['VB-MAPP-12c'],
    level: 3
  },
  {
    id: 'SK-MAND-074',
    domain: 'DOMAIN-MAND',
    name: 'Demonstrates MO generalization (2 reinforcers, same words)',
    labels: ['VB-MAPP-12d'],
    level: 3
  },
  {
    id: 'SK-MAND-075',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to participate in activity 2 times',
    labels: ['VB-MAPP-12e'],
    level: 3
  },
  {
    id: 'SK-MAND-076',
    domain: 'DOMAIN-MAND',
    name: 'Politely mands to stop undesirable activity (5 circumstances)',
    labels: ['VB-MAPP-12M'],
    level: 3
  },
  {
    id: 'SK-MAND-077',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously mands to use bathroom 2 times',
    labels: ['VB-MAPP-13a'],
    level: 3
  },
  {
    id: 'SK-MAND-078',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to attend to own nonverbal behavior 2 times',
    labels: ['VB-MAPP-13b'],
    level: 3
  },
  {
    id: 'SK-MAND-079',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to attend to environment aspect 2 times',
    labels: ['VB-MAPP-13c'],
    level: 3
  },
  {
    id: 'SK-MAND-080',
    domain: 'DOMAIN-MAND',
    name: 'Mands with 2 different adjectives',
    labels: ['VB-MAPP-13d'],
    level: 3
  },
  {
    id: 'SK-MAND-081',
    domain: 'DOMAIN-MAND',
    name: 'Mands with 2 different prepositions',
    labels: ['VB-MAPP-13e', 'ABLLS-F17'],
    level: 3
  },
  {
    id: 'SK-MAND-082',
    domain: 'DOMAIN-MAND',
    name: 'Mands with 2 different adverbs',
    labels: ['VB-MAPP-13f', 'ABLLS-F22'],
    level: 3
  },
  {
    id: 'SK-MAND-083',
    domain: 'DOMAIN-MAND',
    name: 'Mands with 10 different adjectives/prepositions/adverbs',
    labels: ['VB-MAPP-13M'],
    level: 3
  },
  {
    id: 'SK-MAND-084',
    domain: 'DOMAIN-MAND',
    name: 'Mands for sympathy/emotional support 2 times',
    labels: ['VB-MAPP-14a'],
    level: 3
  },
  {
    id: 'SK-MAND-085',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to deliver object to another person 2 times',
    labels: ['VB-MAPP-14b'],
    level: 3
  },
  {
    id: 'SK-MAND-086',
    domain: 'DOMAIN-MAND',
    name: 'Mands for instructions to complete task 2 times',
    labels: ['VB-MAPP-14c', 'ABLLS-F26'],
    level: 3
  },
  {
    id: 'SK-MAND-087',
    domain: 'DOMAIN-MAND',
    name: 'Spontaneously mands with 3 parts of speech in sentence 2 times',
    labels: ['VB-MAPP-14d'],
    level: 3
  },
  {
    id: 'SK-MAND-088',
    domain: 'DOMAIN-MAND',
    name: 'Gives directions/instructions/explanations 5 times',
    labels: ['VB-MAPP-14M'],
    level: 3
  },
  {
    id: 'SK-MAND-089',
    domain: 'DOMAIN-MAND',
    name: 'Mands contain 5 word phrases/sentences 10 times',
    labels: ['VB-MAPP-15a'],
    level: 3
  },
  {
    id: 'SK-MAND-090',
    domain: 'DOMAIN-MAND',
    name: 'Mands for information about future events 2 times',
    labels: ['VB-MAPP-15b', 'ABLLS-F25'],
    level: 3
  },
  {
    id: 'SK-MAND-091',
    domain: 'DOMAIN-MAND',
    name: 'Mands for attention to private event 2 times',
    labels: ['VB-MAPP-15c'],
    level: 3
  },
  {
    id: 'SK-MAND-092',
    domain: 'DOMAIN-MAND',
    name: 'Mands for information 5 times using why questions',
    labels: ['VB-MAPP-15d', 'ABLLS-F28'],
    level: 3
  },
  {
    id: 'SK-MAND-093',
    domain: 'DOMAIN-MAND',
    name: 'Mands for information 5 times using how questions',
    labels: ['VB-MAPP-15e', 'ABLLS-F26'],
    level: 3
  },
  {
    id: 'SK-MAND-094',
    domain: 'DOMAIN-MAND',
    name: 'Mands for others to attend to own intraverbal behavior 5 times',
    labels: ['VB-MAPP-15M'],
    level: 3
  },

  // ==========================================
  // SKILL DOMAIN: TACT/LABELING
  // ==========================================

  // Level 1 Tact Skills
  {
    id: 'SK-TACT-001',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 items with echoic or imitative prompts',
    labels: ['VB-MAPP-1a', 'ABLLS-H1'],
    level: 1
  },
  {
    id: 'SK-TACT-002',
    domain: 'DOMAIN-TACT',
    name: 'Attends to and looks at items that are tacted by others 5 times',
    labels: ['VB-MAPP-1b'],
    level: 1
  },
  {
    id: 'SK-TACT-003',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 1 item without echoic/imitative prompts',
    labels: ['VB-MAPP-1c'],
    level: 1
  },
  {
    id: 'SK-TACT-004',
    domain: 'DOMAIN-TACT',
    name: 'Emits a tact when asked What is it? 2 times',
    labels: ['VB-MAPP-1d'],
    level: 1
  },
  {
    id: 'SK-TACT-005',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 4 items without echoic prompts (may need What is it?)',
    labels: ['VB-MAPP-1M'],
    level: 1
  },
  {
    id: 'SK-TACT-006',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 6 items without echoic/imitative prompts',
    labels: ['VB-MAPP-2a'],
    level: 1
  },
  {
    id: 'SK-TACT-007',
    domain: 'DOMAIN-TACT',
    name: 'Generalizes 2 tacts across 2 different examples',
    labels: ['VB-MAPP-2b'],
    level: 1
  },
  {
    id: 'SK-TACT-008',
    domain: 'DOMAIN-TACT',
    name: 'Spontaneously tacts 2 different items',
    labels: ['VB-MAPP-2c'],
    level: 1
  },
  {
    id: 'SK-TACT-009',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 10 items without echoic prompts',
    labels: ['VB-MAPP-2M'],
    level: 1
  },
  {
    id: 'SK-TACT-010',
    domain: 'DOMAIN-TACT',
    name: 'Spontaneously tacts 5 different items',
    labels: ['VB-MAPP-3a'],
    level: 1
  },
  {
    id: 'SK-TACT-011',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 15 items without echoic prompts',
    labels: ['VB-MAPP-3b'],
    level: 1
  },
  {
    id: 'SK-TACT-012',
    domain: 'DOMAIN-TACT',
    name: 'Generalizes 5 tacts across 2 different examples each',
    labels: ['VB-MAPP-3c'],
    level: 1
  },
  {
    id: 'SK-TACT-013',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 20 items without echoic prompts',
    labels: ['VB-MAPP-3M'],
    level: 1
  },
  {
    id: 'SK-TACT-014',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 3 pictures of common items',
    labels: ['VB-MAPP-4a'],
    level: 1
  },
  {
    id: 'SK-TACT-015',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 people by name',
    labels: ['VB-MAPP-4b'],
    level: 1
  },
  {
    id: 'SK-TACT-016',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 sounds made by common items',
    labels: ['VB-MAPP-4c'],
    level: 1
  },
  {
    id: 'SK-TACT-017',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 body parts on self or others',
    labels: ['VB-MAPP-4d'],
    level: 1
  },
  {
    id: 'SK-TACT-018',
    domain: 'DOMAIN-TACT',
    name: 'Tacts own actions 2 times',
    labels: ['VB-MAPP-4e'],
    level: 1
  },
  {
    id: 'SK-TACT-019',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 25 items without echoic prompts (pictures/people/body parts)',
    labels: ['VB-MAPP-4M'],
    level: 1
  },
  {
    id: 'SK-TACT-020',
    domain: 'DOMAIN-TACT',
    name: 'Spontaneously tacts items/people/body parts 10 times',
    labels: ['VB-MAPP-5a'],
    level: 1
  },
  {
    id: 'SK-TACT-021',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 5 different actions',
    labels: ['VB-MAPP-5b'],
    level: 1
  },
  {
    id: 'SK-TACT-022',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 actions in pictures or videos',
    labels: ['VB-MAPP-5c'],
    level: 1
  },
  {
    id: 'SK-TACT-023',
    domain: 'DOMAIN-TACT',
    name: 'Generalizes 10 tacts across 3 examples each',
    labels: ['VB-MAPP-5d'],
    level: 1
  },
  {
    id: 'SK-TACT-024',
    domain: 'DOMAIN-TACT',
    name: 'Acquires new tact in less than 20 training trials',
    labels: ['VB-MAPP-5e'],
    level: 1
  },
  {
    id: 'SK-TACT-025',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 50 items (animals, food, toys, etc.)',
    labels: ['VB-MAPP-5M'],
    level: 1
  },

  // Level 2 Tact Skills
  {
    id: 'SK-TACT-026',
    domain: 'DOMAIN-TACT',
    name: 'Generalizes tacts across 3 examples of 25 items',
    labels: ['VB-MAPP-6b', 'ABLLS-H15'],
    level: 2
  },
  {
    id: 'SK-TACT-027',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 10 actions (e.g., jumping, sleeping)',
    labels: ['VB-MAPP-6c', 'ABLLS-H12'],
    level: 2
  },
  {
    id: 'SK-TACT-028',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 5 colors',
    labels: ['VB-MAPP-6a', 'ABLLS-H17'],
    level: 2
  },
  {
    id: 'SK-TACT-029',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 shapes',
    labels: ['VB-MAPP-6d', 'ABLLS-H18'],
    level: 2
  },
  {
    id: 'SK-TACT-030',
    domain: 'DOMAIN-TACT',
    name: 'Spontaneously tacts 20 times in 60 minutes',
    labels: ['VB-MAPP-6e'],
    level: 2
  },
  {
    id: 'SK-TACT-031',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 200 nouns and/or verbs in 60 minutes',
    labels: ['VB-MAPP-6M'],
    level: 2
  },
  {
    id: 'SK-TACT-032',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 5 shapes',
    labels: ['VB-MAPP-7a'],
    level: 2
  },
  {
    id: 'SK-TACT-033',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 items with 2 components (noun-verb or noun-adjective)',
    labels: ['VB-MAPP-7b'],
    level: 2
  },
  {
    id: 'SK-TACT-034',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 4 different people by name',
    labels: ['VB-MAPP-7c'],
    level: 2
  },
  {
    id: 'SK-TACT-035',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 8 colors',
    labels: ['VB-MAPP-7d'],
    level: 2
  },
  {
    id: 'SK-TACT-036',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 15 actions',
    labels: ['VB-MAPP-7e'],
    level: 2
  },
  {
    id: 'SK-TACT-037',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 adjectives (big/little)',
    labels: ['VB-MAPP-7f', 'ABLLS-H16'],
    level: 2
  },
  {
    id: 'SK-TACT-038',
    domain: 'DOMAIN-TACT',
    name: 'Tacts items with noun-verb or noun-adjective 25 times',
    labels: ['VB-MAPP-7M'],
    level: 2
  },
  {
    id: 'SK-TACT-039',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 2 prepositions (in/on)',
    labels: ['VB-MAPP-8a', 'ABLLS-H20'],
    level: 2
  },
  {
    id: 'SK-TACT-040',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 4 adjective pairs (big/little, hot/cold)',
    labels: ['VB-MAPP-8b'],
    level: 2
  },
  {
    id: 'SK-TACT-041',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 4 prepositions',
    labels: ['VB-MAPP-8c'],
    level: 2
  },
  {
    id: 'SK-TACT-042',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 10 different emotions/states',
    labels: ['VB-MAPP-8d', 'ABLLS-H32'],
    level: 2
  },
  {
    id: 'SK-TACT-043',
    domain: 'DOMAIN-TACT',
    name: 'Spontaneously tacts 50 times in 60 minutes',
    labels: ['VB-MAPP-8e'],
    level: 2
  },
  {
    id: 'SK-TACT-044',
    domain: 'DOMAIN-TACT',
    name: 'Tacts with 2-word phrases (noun-verb, noun-adjective, verb-adverb)',
    labels: ['VB-MAPP-8M'],
    level: 2
  },
  {
    id: 'SK-TACT-045',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 25 actions',
    labels: ['VB-MAPP-9a'],
    level: 2
  },
  {
    id: 'SK-TACT-046',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 8 adjectives',
    labels: ['VB-MAPP-9b'],
    level: 2
  },
  {
    id: 'SK-TACT-047',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 6 prepositions',
    labels: ['VB-MAPP-9c'],
    level: 2
  },
  {
    id: 'SK-TACT-048',
    domain: 'DOMAIN-TACT',
    name: 'Tacts the category of 5 items given a category name',
    labels: ['VB-MAPP-9d', 'ABLLS-H33'],
    level: 2
  },
  {
    id: 'SK-TACT-049',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 10 sounds made by animals or objects',
    labels: ['VB-MAPP-9e'],
    level: 2
  },
  {
    id: 'SK-TACT-050',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 1000 nouns/verbs/adjectives/prepositions',
    labels: ['VB-MAPP-9M'],
    level: 2
  },

  // Level 3 Tact Skills
  {
    id: 'SK-TACT-051',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 10 adverbs (quickly, softly)',
    labels: ['VB-MAPP-10a', 'ABLLS-H21'],
    level: 3
  },
  {
    id: 'SK-TACT-052',
    domain: 'DOMAIN-TACT',
    name: 'Tacts with 3-word phrases (noun-verb-adjective)',
    labels: ['VB-MAPP-10b'],
    level: 3
  },
  {
    id: 'SK-TACT-053',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 10 pronouns',
    labels: ['VB-MAPP-10c', 'ABLLS-H26'],
    level: 3
  },
  {
    id: 'SK-TACT-054',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 50 actions',
    labels: ['VB-MAPP-10d'],
    level: 3
  },
  {
    id: 'SK-TACT-055',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 20 adjectives',
    labels: ['VB-MAPP-10e'],
    level: 3
  },
  {
    id: 'SK-TACT-056',
    domain: 'DOMAIN-TACT',
    name: 'Tacts noun-verb-adjective using 1000 different combinations',
    labels: ['VB-MAPP-10M'],
    level: 3
  },
  {
    id: 'SK-TACT-057',
    domain: 'DOMAIN-TACT',
    name: 'Tacts using complete sentences 50 times',
    labels: ['VB-MAPP-11a'],
    level: 3
  },
  {
    id: 'SK-TACT-058',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 4 different categories of items',
    labels: ['VB-MAPP-11b'],
    level: 3
  },
  {
    id: 'SK-TACT-059',
    domain: 'DOMAIN-TACT',
    name: 'Tacts with 4-word phrases using all verb tenses',
    labels: ['VB-MAPP-11c'],
    level: 3
  },
  {
    id: 'SK-TACT-060',
    domain: 'DOMAIN-TACT',
    name: 'Describes objects by feature, function, or class',
    labels: ['VB-MAPP-11d', 'ABLLS-H36'],
    level: 3
  },
  {
    id: 'SK-TACT-061',
    domain: 'DOMAIN-TACT',
    name: 'Spontaneously tacts 100 times per day',
    labels: ['VB-MAPP-11e'],
    level: 3
  },
  {
    id: 'SK-TACT-062',
    domain: 'DOMAIN-TACT',
    name: 'Tacts using noun-verb-preposition or noun-verb-adjective 25 times',
    labels: ['VB-MAPP-11M'],
    level: 3
  },
  {
    id: 'SK-TACT-063',
    domain: 'DOMAIN-TACT',
    name: 'Tacts past events accurately',
    labels: ['VB-MAPP-12a'],
    level: 3
  },
  {
    id: 'SK-TACT-064',
    domain: 'DOMAIN-TACT',
    name: 'Tacts own behavior or actions to others',
    labels: ['VB-MAPP-12b'],
    level: 3
  },
  {
    id: 'SK-TACT-065',
    domain: 'DOMAIN-TACT',
    name: 'Tacts others behaviors/actions accurately',
    labels: ['VB-MAPP-12c'],
    level: 3
  },
  {
    id: 'SK-TACT-066',
    domain: 'DOMAIN-TACT',
    name: 'Tacts with possessive pronouns',
    labels: ['VB-MAPP-12d', 'ABLLS-H27'],
    level: 3
  },
  {
    id: 'SK-TACT-067',
    domain: 'DOMAIN-TACT',
    name: 'Uses complete sentences with 5+ words 100 times',
    labels: ['VB-MAPP-12M'],
    level: 3
  },
  {
    id: 'SK-TACT-068',
    domain: 'DOMAIN-TACT',
    name: 'Tacts environmental sounds accurately',
    labels: ['VB-MAPP-13a'],
    level: 3
  },
  {
    id: 'SK-TACT-069',
    domain: 'DOMAIN-TACT',
    name: 'Tacts private events (emotions, physical states)',
    labels: ['VB-MAPP-13b'],
    level: 3
  },
  {
    id: 'SK-TACT-070',
    domain: 'DOMAIN-TACT',
    name: 'Uses correct subject-verb agreement',
    labels: ['VB-MAPP-13c'],
    level: 3
  },
  {
    id: 'SK-TACT-071',
    domain: 'DOMAIN-TACT',
    name: 'Tacts 10 community helpers/jobs',
    labels: ['VB-MAPP-13d', 'ABLLS-H38'],
    level: 3
  },
  {
    id: 'SK-TACT-072',
    domain: 'DOMAIN-TACT',
    name: 'Creates novel tacts by combining parts of speech',
    labels: ['VB-MAPP-13M'],
    level: 3
  },
  {
    id: 'SK-TACT-073',
    domain: 'DOMAIN-TACT',
    name: 'Describes events using sequence (first, then, last)',
    labels: ['VB-MAPP-14a', 'ABLLS-H40'],
    level: 3
  },
  {
    id: 'SK-TACT-074',
    domain: 'DOMAIN-TACT',
    name: 'Tacts story elements from books',
    labels: ['VB-MAPP-14b'],
    level: 3
  },
  {
    id: 'SK-TACT-075',
    domain: 'DOMAIN-TACT',
    name: 'Gives extended descriptions of items or events',
    labels: ['VB-MAPP-14c'],
    level: 3
  },
  {
    id: 'SK-TACT-076',
    domain: 'DOMAIN-TACT',
    name: 'Uses comparative/superlative adjectives',
    labels: ['VB-MAPP-14d', 'ABLLS-H19'],
    level: 3
  },
  {
    id: 'SK-TACT-077',
    domain: 'DOMAIN-TACT',
    name: 'Describes complex scenes with multiple elements',
    labels: ['VB-MAPP-14M'],
    level: 3
  },
  {
    id: 'SK-TACT-078',
    domain: 'DOMAIN-TACT',
    name: 'Narrates a story from memory',
    labels: ['VB-MAPP-15a'],
    level: 3
  },
  {
    id: 'SK-TACT-079',
    domain: 'DOMAIN-TACT',
    name: 'Tacts abstract concepts (time, money, measurement)',
    labels: ['VB-MAPP-15b'],
    level: 3
  },
  {
    id: 'SK-TACT-080',
    domain: 'DOMAIN-TACT',
    name: 'Uses creative language (similes, metaphors)',
    labels: ['VB-MAPP-15c'],
    level: 3
  },
  {
    id: 'SK-TACT-081',
    domain: 'DOMAIN-TACT',
    name: 'Provides detailed explanations of processes',
    labels: ['VB-MAPP-15d'],
    level: 3
  },
  {
    id: 'SK-TACT-082',
    domain: 'DOMAIN-TACT',
    name: 'Tacts in connected discourse across multiple sentences',
    labels: ['VB-MAPP-15M'],
    level: 3
  }
];

// Helper functions

// Get all skills for a specific domain
export const getSkillsByDomain = (domainId) => {
  return skillsData.filter(skill => skill.domain === domainId);
};

// Get domain by ID
export const getDomainById = (domainId) => {
  return skillDomains.find(domain => domain.id === domainId);
};

// Get domain by code (e.g., 'MAND', 'TACT')
export const getDomainByCode = (code) => {
  return skillDomains.find(domain => domain.code === code);
};

// Get skill by its unique ID (e.g., 'SK-MAND-001')
export const getSkillById = (skillId) => {
  return skillsData.find(skill => skill.id === skillId);
};

// Get skills by assessment label (e.g., 'VB-MAPP-1a', 'ABLLS-G1')
export const getSkillsByLabel = (label) => {
  return skillsData.filter(skill => skill.labels.includes(label));
};

// Get skills by level (1, 2, or 3)
export const getSkillsByLevel = (level) => {
  return skillsData.filter(skill => skill.level === level);
};

// Get skills by domain and level
export const getSkillsByDomainAndLevel = (domainId, level) => {
  return skillsData.filter(skill => skill.domain === domainId && skill.level === level);
};

// Get skills by assessment source (e.g., 'VB-MAPP', 'ABLLS')
export const getSkillsByAssessmentSource = (source) => {
  return skillsData.filter(skill =>
    skill.labels.some(label => label.startsWith(source))
  );
};

// Search skills by query (searches in id, name, and labels)
export const searchSkills = (query) => {
  const lowerQuery = query.toLowerCase();
  return skillsData.filter(skill =>
    skill.id.toLowerCase().includes(lowerQuery) ||
    skill.name.toLowerCase().includes(lowerQuery) ||
    skill.labels.some(label => label.toLowerCase().includes(lowerQuery))
  );
};

// Get all assessment labels used in skills
export const getAllLabels = () => {
  const labelsSet = new Set();
  skillsData.forEach(skill => {
    skill.labels.forEach(label => labelsSet.add(label));
  });
  return Array.from(labelsSet).sort();
};

// Get skill count by domain
export const getSkillCountByDomain = () => {
  const counts = {};
  skillDomains.forEach(domain => {
    counts[domain.id] = skillsData.filter(skill => skill.domain === domain.id).length;
  });
  return counts;
};

export default {
  skillDomains,
  skillsData,
  getSkillsByDomain,
  getDomainById,
  getDomainByCode,
  getSkillById,
  getSkillsByLabel,
  getSkillsByLevel,
  getSkillsByDomainAndLevel,
  getSkillsByAssessmentSource,
  searchSkills,
  getAllLabels,
  getSkillCountByDomain
};
